import React, { useEffect, useRef, useMemo } from 'react';
import {
    createChart,
    IChartApi,
    ISeriesApi,
    ColorType,
    CrosshairMode,
    CandlestickData,
    LineData,
    HistogramData,
    CandlestickSeries,
    LineSeries,
    AreaSeries,
    HistogramSeries,
    createSeriesMarkers,
    ISeriesMarkersPluginApi,
    SeriesMarker,
    Time,
} from 'lightweight-charts';
import { useTheme } from '../../context/ThemeContext';
import ChartShareButton from '../Common/ChartShareButton';
import DrawingToolbar from './DrawingToolbar'; // Import Toolbar

// ---------------------------------------------------------------------------
// TYPES
// ---------------------------------------------------------------------------
export type ChartType = 'line' | 'candlestick' | 'heikin-ashi' | 'area';

export interface OverlayData {
    sma20?: LineData[];
    sma50?: LineData[];
    sma200?: LineData[];
    bollinger?: {
        upper: LineData[];
        lower: LineData[];
        sma?: LineData[]; // Middle band
    };
}

// Drawing Interfaces
interface DrawingPoint {
    time: any; // Using any for flexibility (string | number) compatible with lib
    price: number;
}

interface Trendline {
    id: string;
    points: [DrawingPoint, DrawingPoint];
    type: 'trendline';
}

interface DrawingState {
    activeTool: 'cursor' | 'trendline' | 'horizontal';
    drawings: Trendline[];
    currentDrawing: Partial<Trendline> | null;
}

/** Événement sur titres à repérer sur l'axe temps. */
export interface MarqueurEvenement {
    date_effective: string;
    type_evenement: 'FRACTIONNEMENT' | 'AUGMENTATION_CAPITAL' | 'PAIEMENT_DIVIDENDE';
    facteur_cours: number | null;
    libelle: string;
}

export interface AdvancedChartProps {
    data: CandlestickData[]; // Expecting generic OHLC data
    /**
     * Repères d'opérations sur titres. OPTIONNEL et STRICTEMENT ADDITIF : absent ou
     * vide, le graphique est rigoureusement identique à ce qu'il était.
     *
     * Sert à VÉRIFIER la série de cours : le marqueur dit où une discontinuité doit
     * apparaître si les cours sont d'époque, et où la courbe doit au contraire rester
     * continue une fois ajustée. Sans lui, une marche sur la courbe est indécidable —
     * vrai mouvement de marché, ou ajustement défaillant ?
     */
    marqueurs?: MarqueurEvenement[];
    volumeData?: HistogramData[]; // Optional volume data
    overlayData?: OverlayData; // New: Pre-calculated overlay series
    height?: number;
    chartType: ChartType;
    overlays: {
        sma20: boolean;
        sma50: boolean;
        sma200: boolean;
        bollinger: boolean;
        volume: boolean;
    };
    colors?: {
        up: string;
        down: string;
        bg: string;
        text: string;
        grid: string;
    };
}

// ---------------------------------------------------------------------------
// HELPER: Heikin-Ashi Calculator
// ---------------------------------------------------------------------------
const calculateHeikinAshi = (data: CandlestickData[]): CandlestickData[] => {
    const haData: CandlestickData[] = [];
    let prevOpen = 0;
    let prevClose = 0;

    data.forEach((item, index) => {
        const open = Number(item.open);
        const high = Number(item.high);
        const low = Number(item.low);
        const close = Number(item.close);

        const haClose = (open + high + low + close) / 4;
        const haOpen = index === 0 ? (open + close) / 2 : (prevOpen + prevClose) / 2;
        const haHigh = Math.max(high, haOpen, haClose);
        const haLow = Math.min(low, haOpen, haClose);

        haData.push({
            time: item.time,
            open: haOpen,
            high: haHigh,
            low: haLow,
            close: haClose,
        });

        prevOpen = haOpen;
        prevClose = haClose;
    });

    return haData;
};

// ---------------------------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------------------------
const AdvancedChart: React.FC<AdvancedChartProps> = ({
    data,
    marqueurs,
    volumeData = [],
    overlayData = {},
    height = 500,
    chartType,
    overlays,
    colors
}) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | null>(null);
    const mainSeriesRef = useRef<ISeriesApi<any> | null>(null);
    const markersPluginRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    const activeColors = {
        up: colors?.up || '#26a69a',
        down: colors?.down || '#ef5350',
        bg: colors?.bg && !colors.bg.includes('var') ? colors.bg : (isDark ? '#0a2128' : '#ffffff'),
        text: colors?.text && !colors.text.includes('var') ? colors.text : (isDark ? '#d3e2e8' : '#333333'),
        grid: colors?.grid && !colors.grid.includes('var') ? colors.grid : (isDark ? 'rgba(211, 226, 232, 0.05)' : 'rgba(156, 163, 175, 0.1)'),
    };

    // Keep track of overlay series to remove them cleanly
    const overlaySeriesRefs = useRef<{ [key: string]: ISeriesApi<any> }>({});

    // --- Drawing State ---
    const [drawingState, setDrawingState] = React.useState<DrawingState>({
        activeTool: 'cursor',
        drawings: [],
        currentDrawing: null,
    });

    // Force re-render on chart resize/scroll to update SVG lines
    const [chartVersion, setChartVersion] = React.useState(0);

    // Helper to get time/price from mouse coordinates
    const getChartCoordinates = (e: React.MouseEvent) => {
        if (!chartRef.current || !mainSeriesRef.current || !chartContainerRef.current) return null;

        const rect = chartContainerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const time = chartRef.current.timeScale().coordinateToTime(x);
        const price = mainSeriesRef.current.coordinateToPrice(y);

        if (time === null || price === null) return null;
        return { time, price };
    };

    const handleChartClick = (e: React.MouseEvent) => {
        if (drawingState.activeTool === 'cursor') return;

        const coords = getChartCoordinates(e);
        if (!coords) return;

        if (drawingState.activeTool === 'trendline') {
            if (!drawingState.currentDrawing) {
                // Start drawing
                setDrawingState(prev => ({
                    ...prev,
                    currentDrawing: {
                        id: Date.now().toString(),
                        type: 'trendline',
                        points: [coords, coords] as [DrawingPoint, DrawingPoint] // Init both points same
                    }
                }));
            } else {
                // Finish drawing
                const current = drawingState.currentDrawing!;
                setDrawingState(prev => ({
                    ...prev,
                    drawings: [...prev.drawings, { ...current, points: [current.points![0], coords] } as Trendline],
                    currentDrawing: null
                }));
            }
        }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (drawingState.currentDrawing) {
            const coords = getChartCoordinates(e);
            if (!coords) return;

            // Update the second point dynamically
            const current = drawingState.currentDrawing!;
            setDrawingState(prev => ({
                ...prev,
                currentDrawing: {
                    ...current,
                    points: [current.points![0], coords] as [DrawingPoint, DrawingPoint]
                }
            }));
        }
    };

    // --- SVG Rendering Logic ---
    // We map stored Time/Price back to X/Y coordinates
    const mapDrawingToSVG = (drawing: Trendline | Partial<Trendline>) => {
        if (!chartRef.current || !mainSeriesRef.current || !drawing.points) return null;

        const p1 = drawing.points[0];
        const p2 = drawing.points[1];

        if (!p1 || !p2) return null;

        const x1 = chartRef.current.timeScale().timeToCoordinate(p1.time);
        const y1 = mainSeriesRef.current.priceToCoordinate(p1.price);
        const x2 = chartRef.current.timeScale().timeToCoordinate(p2.time);
        const y2 = mainSeriesRef.current.priceToCoordinate(p2.price);

        if (x1 === null || y1 === null || x2 === null || y2 === null) return null;

        return (
            <line
                key={drawing.id || 'preview'}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#2962FF"
                strokeWidth="2"
            />
        );
    };

    // 1. Prepare Data based on Chart Type
    const displayData = useMemo(() => {
        if (!data || data.length === 0) return [];

        if (chartType === 'heikin-ashi') {
            return calculateHeikinAshi(data);
        }
        return data;
    }, [data, chartType]);

    // 2. Initialize / Update Chart
    useEffect(() => {
        if (!chartContainerRef.current) return;
        if (displayData.length === 0) return;

        // --- Init Chart Instance if needed ---
        if (!chartRef.current) {
            chartRef.current = createChart(chartContainerRef.current, {
                width: chartContainerRef.current.clientWidth,
                height: height,
                layout: {
                    background: { type: ColorType.Solid, color: activeColors.bg },
                    textColor: activeColors.text,
                },
                grid: {
                    vertLines: { color: activeColors.grid },
                    horzLines: { color: activeColors.grid },
                },
                rightPriceScale: {
                    borderColor: activeColors.grid,
                },
                timeScale: {
                    borderColor: activeColors.grid,
                    timeVisible: false,
                    fixLeftEdge: true,
                    fixRightEdge: true,
                    minBarSpacing: 0.5,
                },
                crosshair: {
                    mode: CrosshairMode.Normal,
                },
            });

            // Handle Resize
            const handleResize = () => {
                if (chartContainerRef.current && chartRef.current) {
                    chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
                    setChartVersion(v => v + 1); // Trigger React re-render for SVG overlay
                }
            };
            window.addEventListener('resize', handleResize);

            // Subscribe to visible range changes to update SVG
            chartRef.current.timeScale().subscribeVisibleTimeRangeChange(() => {
                setChartVersion(v => v + 1);
            });
        }

        const chart = chartRef.current;

        // Remove old main series
        if (mainSeriesRef.current) {
            chart.removeSeries(mainSeriesRef.current);
            mainSeriesRef.current = null;
        }

        // --- Create Main Series based on Type ---
        let series: ISeriesApi<any>;

        if (chartType === 'area') {
            series = chart.addSeries(AreaSeries, {
                lineColor: '#2962FF',
                topColor: 'rgba(41, 98, 255, 0.3)',
                bottomColor: 'rgba(41, 98, 255, 0)',
                lineWidth: 2,
            });
            // Area needs LineData (value)
            const lineData = displayData.map(d => ({ time: d.time, value: d.close }));
            series.setData(lineData);

        } else if (chartType === 'line') {
            series = chart.addSeries(LineSeries, {
                color: '#2962FF',
                lineWidth: 2,
            });
            const lineData = displayData.map(d => ({ time: d.time, value: d.close }));
            series.setData(lineData);

        } else {
            // Candlestick OR Heikin-Ashi (displayData is already prepared)
            series = chart.addSeries(CandlestickSeries, {
                upColor: activeColors.up,
                downColor: activeColors.down,
                borderVisible: false,
                wickUpColor: activeColors.up,
                wickDownColor: activeColors.down,
            });
            series.setData(displayData);
        }

        mainSeriesRef.current = series;

        // --- Repères d'opérations sur titres (optionnels, purement additifs) ---
        // Placés SOUS la bougie et en petit : ce sont des repères de contrôle, ils ne
        // doivent jamais concurrencer la lecture du cours. Le détachement de dividende
        // est volontairement plus discret que le fractionnement — il ne décale pas la
        // série, il ne mérite donc pas le même poids visuel.
        // ⚠️ NE PAS toucher l'ancien plugin : sa série a déjà été supprimée plus haut
        // (`chart.removeSeries`), et l'appeler serait un usage après libération. Une
        // exception ici avorterait silencieusement la suite de l'effet — donc la
        // création des nouveaux marqueurs. Le plugin disparaît avec sa série ; il
        // suffit d'oublier la référence.
        markersPluginRef.current = null;

        if (marqueurs && marqueurs.length > 0) {
            const style: Record<string, { color: string; shape: 'arrowUp' | 'circle' | 'square' }> = {
                FRACTIONNEMENT: { color: '#7c3aed', shape: 'arrowUp' },
                AUGMENTATION_CAPITAL: { color: '#0891b2', shape: 'square' },
                PAIEMENT_DIVIDENDE: { color: '#94a3b8', shape: 'circle' },
            };
            const liste: SeriesMarker<Time>[] = marqueurs
                .map((m) => {
                    const st = style[m.type_evenement] ?? style.PAIEMENT_DIVIDENDE;
                    return {
                        time: m.date_effective as Time,
                        position: 'belowBar' as const,
                        shape: st.shape,
                        color: st.color,
                        text: m.libelle,
                        id: `${m.type_evenement}-${m.date_effective}`,
                        // ⚠️ 'top' EST NECESSAIRE EN MODE AIRE. Par defaut le marqueur
                        // est dessine sous la serie : le remplissage de l'AreaSeries le
                        // recouvre entierement et il devient invisible. Constate sur
                        // SOLIBRA — le repere apparaissait en mode Ligne, jamais en Aire.
                        zOrder: 'top' as const,
                    };
                })
                // lightweight-charts EXIGE des marqueurs tries par temps croissant :
                // un ordre quelconque les fait disparaitre sans erreur.
                .sort((a, b) => String(a.time).localeCompare(String(b.time)));
            try {
                markersPluginRef.current = createSeriesMarkers(series, liste);
            } catch (e) {
                // Un repère qui échoue ne doit JAMAIS emporter le graphique : c'est un
                // outil de contrôle, pas la donnée. On trace et on continue.
                console.warn('AdvancedChart: marqueurs non appliqués', e);
                markersPluginRef.current = null;
            }
        }

        // --- Clean Indicators ---
        Object.values(overlaySeriesRefs.current).forEach(s => chart.removeSeries(s));
        overlaySeriesRefs.current = {};

        // --- Add Overlays from Props ---
        if (overlays.sma20 && overlayData.sma20) {
            const s = chart.addSeries(LineSeries, { color: '#ff9800', lineWidth: 2, title: 'SMA 20' });
            s.setData(overlayData.sma20);
            overlaySeriesRefs.current['sma20'] = s;
        }
        if (overlays.sma50 && overlayData.sma50) {
            const s = chart.addSeries(LineSeries, { color: '#00C853', lineWidth: 2, title: 'SMA 50' });
            s.setData(overlayData.sma50);
            overlaySeriesRefs.current['sma50'] = s;
        }
        if (overlays.sma200 && overlayData.sma200) {
            const s = chart.addSeries(LineSeries, { color: '#f44336', lineWidth: 2, title: 'SMA 200' });
            s.setData(overlayData.sma200);
            overlaySeriesRefs.current['sma200'] = s;
        }
        if (overlays.bollinger && overlayData.bollinger) {
            // Upper
            const u = chart.addSeries(LineSeries, { color: 'rgba(41, 98, 255, 0.5)', lineWidth: 1, title: 'BB Upper' });
            u.setData(overlayData.bollinger.upper);
            overlaySeriesRefs.current['bbUpper'] = u;
            // Lower
            const l = chart.addSeries(LineSeries, { color: 'rgba(41, 98, 255, 0.5)', lineWidth: 1, title: 'BB Lower' });
            l.setData(overlayData.bollinger.lower);
            overlaySeriesRefs.current['bbLower'] = l;
        }

        // --- Add Volume (Overlay at bottom) ---
        if (overlays.volume && volumeData.length > 0) {
            const volSeries = chart.addSeries(HistogramSeries, {
                color: '#26a69a',
                priceFormat: {
                    type: 'volume',
                },
                priceScaleId: '', // Use 'overlay' logic by setting scaleMargins
            });

            // Configure Volume to sit at the bottom 15% of the chart
            volSeries.priceScale().applyOptions({
                scaleMargins: {
                    top: 0.85, // Highest volume bar reaches 85% down from top (so it occupies bottom 15%)
                    bottom: 0,
                },
            });

            volSeries.setData(volumeData);
            overlaySeriesRefs.current['volume'] = volSeries;
        }

        // Fit content to show the full range of data
        chart.timeScale().fitContent();

    }, [displayData, marqueurs, volumeData, overlayData, chartType, overlays, height, colors, theme]);

    return (
        <div
            style={{ position: 'relative' }}
            onMouseMove={handleMouseMove}
        >
            <DrawingToolbar
                activeTool={drawingState.activeTool}
                onToolChange={(tool: any) => setDrawingState(prev => ({ ...prev, activeTool: tool }))}
                onClear={() => setDrawingState(prev => ({ ...prev, drawings: [] }))}
            />

            <div
                ref={chartContainerRef}
                onClick={handleChartClick}
                style={{ width: '100%', height: `${height}px`, cursor: drawingState.activeTool !== 'cursor' ? 'crosshair' : 'default' }}
            />

            {/* SVG Overlay */}
            <svg
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none', // Allow clicks to pass through to the div below unless we intercept
                    zIndex: 10
                }}
            >
                {drawingState.drawings.map(d => mapDrawingToSVG(d))}
                {drawingState.currentDrawing && mapDrawingToSVG(drawingState.currentDrawing)}
            </svg>

            <ChartShareButton
                targetRef={chartContainerRef}
                fileName={`chart_analysis`}
                style={{ position: 'absolute', top: 10, right: 60, zIndex: 10 }}
            />
        </div>
    );
};

export default AdvancedChart;
